import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';
import YandexEventsPanel from './YandexEventsPanel';

const YandexTicketsPanel = ({ token }) => {
    const [tabValue, setTabValue] = useState(0);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Форматируем даты для input type="date" (YYYY-MM-DD)
    const getDateString = (daysAgo = 0) => {
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        return date.toISOString().split('T')[0];
    };

    const [startDate, setStartDate] = useState(getDateString(7)); // 7 дней назад
    const [endDate, setEndDate] = useState(getDateString(0)); // сегодня
    const [stats, setStats] = useState({
        totalOrders: 0,
        totalTickets: 0,
        totalSum: 0,
        completedOrders: 0,
        cancelledOrders: 0,
        reservedOrders: 0,
        deliveryOrders: 0
    });

    const cityId = '46677457'; // Ваш city_id

    const fetchOrders = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/yandex/sold-tickets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    city_id: cityId,
                    start_date: startDate,
                    end_date: endDate
                })
            });

            const result = await response.json();
            // Диагностика структуры ответа
            // console.log('Yandex sold-tickets result:', result);

            // Новый формат: success + data.result
            if (result?.success && result?.data && Array.isArray(result.data.result)) {
                // Преобразуем массив массивов в плоский массив заказов
                const flatOrders = result.data.result.flat();
                setOrders(flatOrders);

                // Вычисляем статистику
                const totalOrders = flatOrders.length;
                const soldOrders = flatOrders.filter(order => order.status === 1);
                const totalTickets = soldOrders.reduce((sum, order) => sum + order.tickets_count, 0);
                const totalSum = soldOrders.reduce((sum, order) => sum + order.sum, 0);
                const completedOrders = soldOrders.length;
                const cancelledOrders = flatOrders.filter(order => order.status === 0).length;
                const reservedOrders = flatOrders.filter(order => order.status === 2).length;
                const deliveryOrders = flatOrders.filter(order => order.status === 3).length;

                setStats({
                    totalOrders,
                    totalTickets,
                    totalSum,
                    completedOrders,
                    cancelledOrders,
                    reservedOrders,
                    deliveryOrders
                });
            } else {
                const errText = result?.message || result?.data?.error || result?.error || result?.data?.message;
                setError('Ошибка при получении данных: ' + (errText || JSON.stringify(result?.attempts || result).slice(0, 300)));
            }
        } catch (err) {
            setError('Ошибка подключения: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusChip = (status, isReturned) => {
        if (isReturned === 1) {
            return <Chip label="Возврат" color="error" size="small" />;
        }

        switch (status) {
            case 0:
                return <Chip label="❌ Аннулирован" color="error" size="small" />;
            case 1:
                return <Chip label="✅ Продан" color="success" size="small" />;
            case 2:
                return <Chip label="📋 Бронь" color="info" size="small" />;
            case 3:
                return <Chip label="🚚 Доставка" color="warning" size="small" />;
            default:
                return <Chip label="❓ Неизвестно" color="default" size="small" />;
        }
    };

    return (
        <Box>
            {/* Табы */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab label="Заказы" />
                    <Tab label="События" />
                </Tabs>
            </Box>

            {/* Контент табов */}
            {tabValue === 1 ? (
                <YandexEventsPanel token={token} />
            ) : (
                <>
                    {/* Фильтры */}
                    <Paper
                        sx={{
                            p: 3,
                            mb: 3,
                            background: 'linear-gradient(135deg, #ffffff 0%, #fef3c7 100%)',
                            boxShadow: '0 4px 20px rgba(245, 158, 11, 0.15)',
                            borderRadius: 3,
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Дата начала"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'white',
                                            '&:hover fieldset': {
                                                borderColor: '#f59e0b',
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Дата окончания"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: 'white',
                                            '&:hover fieldset': {
                                                borderColor: '#f59e0b',
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Button
                                    variant="contained"
                                    onClick={fetchOrders}
                                    disabled={loading}
                                    fullWidth
                                    sx={{
                                        height: 56,
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        fontWeight: 600,
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                                        },
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : '🔄 Обновить'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Статистика */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                boxShadow: '0 4px 15px rgba(245, 158, 11, 0.2)',
                                transition: 'transform 0.3s',
                                '&:hover': { transform: 'translateY(-5px)' }
                            }}>
                                <CardContent>
                                    <Typography color="#78350f" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                        📦 Всего заказов
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                                        {stats.totalOrders}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
                                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.2)',
                                transition: 'transform 0.3s',
                                '&:hover': { transform: 'translateY(-5px)' }
                            }}>
                                <CardContent>
                                    <Typography color="#064e3b" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                        ✅ Продано
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#10b981' }}>
                                        {stats.completedOrders}
                                    </Typography>
                                    <Typography variant="caption" color="#064e3b" sx={{ fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                                        {stats.cancelledOrders > 0 && `❌ ${stats.cancelledOrders} аннул.`}
                                        {stats.reservedOrders > 0 && ` 📋 ${stats.reservedOrders} бронь`}
                                        {stats.deliveryOrders > 0 && ` 🚚 ${stats.deliveryOrders} дост.`}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                boxShadow: '0 4px 15px rgba(59, 130, 246, 0.2)',
                                transition: 'transform 0.3s',
                                '&:hover': { transform: 'translateY(-5px)' }
                            }}>
                                <CardContent>
                                    <Typography color="#1e3a8a" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                        🎫 Билетов продано
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                                        {stats.totalTickets}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #fae8ff 0%, #f3e8ff 100%)',
                                boxShadow: '0 4px 15px rgba(168, 85, 247, 0.2)',
                                transition: 'transform 0.3s',
                                '&:hover': { transform: 'translateY(-5px)' }
                            }}>
                                <CardContent>
                                    <Typography color="#581c87" gutterBottom sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                        💰 Сумма продаж
                                    </Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#a855f7', fontSize: '1.8rem' }}>
                                        {stats.totalSum.toLocaleString('ru-RU')} ₽
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Ошибки */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Таблица заказов */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            borderRadius: 3,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            background: 'white',
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>№ Заказа</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Дата</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Клиент</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Телефон</TableCell>
                                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Email</TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Билетов</TableCell>
                                    <TableCell align="right" sx={{ color: 'white', fontWeight: 700 }}>Сумма</TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Статус</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : orders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography variant="body1" color="textSecondary">
                                                Заказы не найдены
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    orders.map((order) => (
                                        <TableRow key={order.id} hover>
                                            <TableCell>{order.id}</TableCell>
                                            <TableCell>{formatDate(order.order_date)}</TableCell>
                                            <TableCell>{order.customer?.name || 'Не указано'}</TableCell>
                                            <TableCell>{order.customer?.phone || '-'}</TableCell>
                                            <TableCell>{order.customer?.email || '-'}</TableCell>
                                            <TableCell align="center">{order.tickets_count}</TableCell>
                                            <TableCell align="right">
                                                <strong>{order.sum.toLocaleString('ru-RU')} ₽</strong>
                                            </TableCell>
                                            <TableCell align="center">
                                                {getStatusChip(order.status, order.is_returned)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default YandexTicketsPanel;
